export type SmsMessageStatus = 'pending' | 'sent' | 'failed' | 'delivered';

export interface SmsMessage {
  id?: string;
  recipient: string;
  message: string;
  status?: SmsMessageStatus;
  segments?: number;
  cost?: number;
  creditsUsed?: number;
  createdAt?: string;
}

export interface SmsSendRequest {
  recipient?: string;
  recipients?: string[];
  message: string;
  templateId?: string;
  idempotency_key?: string;
}

export interface SmsSendResult {
  success: boolean;
  message_id?: string;
  recipient?: string;
  segments?: number;
  cost?: number;
  status?: string;
  test_mode?: boolean;
  batchId?: string;
  sentCount?: number;
  failedCount?: number;
  error?: string;
}

export interface SmsMultipleSimpleRequest {
  recipients: string[];
  message: string;
}

export interface SmsMultipleSimpleItemResult {
  recipient: string;
  success: boolean;
  message_id?: string;
  status?: string;
  segments?: number;
  cost?: number;
  error?: string;
  error_code?: string;
  error_message?: string;
  provider_message?: string | null;
}

export interface SmsMultipleSimpleResult {
  total: number;
  sent: number;
  failed: number;
  results: SmsMultipleSimpleItemResult[];
}

export interface SmsFileRow {
  recipient: string;
  message: string;
  sourceRow?: number;
}

export interface SmsFileSendItemResult extends SmsMultipleSimpleItemResult {
  message: string;
  sourceRow?: number;
}

export interface SmsFileSendResult {
  total: number;
  sent: number;
  failed: number;
  totalSegments: number;
  totalCost: number;
  results: SmsFileSendItemResult[];
}

export interface AdminSmsMessageProfile {
  full_name: string | null;
  email: string | null;
  razon_social: string | null;
  ruc: string | null;
}

export interface SmsProviderResponse {
  test_mode?: boolean;
  provider?: string;
  provider_name?: string;
  [key: string]: unknown;
}

export interface AdminSmsMessage {
  source?: 'api' | 'platform' | null;
  campaign_id?: string | null;
  campaign_name?: string | null;
  id: string;
  user_id: string;
  recipient: string;
  message: string;
  segments: number;
  cost: number;
  status: SmsMessageStatus | 'expired';
  provider_message_id: string | null;
  provider_response: SmsProviderResponse | null;
  error_message: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  created_at: string;
  profile: AdminSmsMessageProfile | null;
  attempt: AdminSmsSendAttempt | null;
}

export interface AdminSmsSendAttempt {
  id: string;
  sms_message_id: string | null;
  idempotency_key: string;
  provider_recipient: string | null;
  status: 'processing' | 'sent' | 'failed';
  provider: string | null;
  provider_response: SmsProviderResponse | null;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface AdminMessageStats {expired?:number;total:number;sent:number;delivered:number;failed:number;consumedSms:number;totalCost:number;}
export interface AdminMessageOptions {users:{id:string;label:string}[];campaigns:{id:string;name:string}[];}
export interface AdminMessagePage {messages:AdminSmsMessage[];stats:AdminMessageStats;}
export interface AdminSmsMessageFilters {
  search?: string;
  userId?: string;
  campaign?: string;
  page?: number;
  pageSize?: number;
  status?: SmsMessageStatus | 'expired' | 'all';
  dateFrom?: string | null;
  dateTo?: string | null;
  limit?: number;
}

export interface ClientSmsMessage {
  id: string;
  user_id: string;
  recipient: string;
  message: string;
  segments: number;
  cost: number;
  status: SmsMessageStatus;
  provider_message_id: string | null;
  provider_response: SmsProviderResponse | null;
  error_message: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  created_at: string;
  attempt: AdminSmsSendAttempt | null;
}

export interface ClientSmsStats {
  total: number;
  sent: number;
  delivered: number;
  failed: number;
  pending: number;
  consumedSegments: number;
  totalCost: number;
}

export type ProviderHistoryStatus = SmsMessageStatus | 'expired' | 'unconfirmed' | 'unknown';
export interface ProviderHistoryMessage {
  source?: 'platform' | 'api';
  api_name?: string | null;
  id: string;
  request_id: string;
  recipient: string;
  message: string;
  status: ProviderHistoryStatus;
  provider_status: string;
  operator: string | null;
  segments: number | null;
  cost: number | null;
  created_at: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  error_message: string | null;
}

export interface AdminProviderDeliverySummary {
  success: boolean;
  total: number;
  delivered: number;
  failed: number;
  expired: number;
  unconfirmed: number;
  queriedCompanies: number;
  failedCompanies: number;
  complete: boolean;
  failedRucs?: string[];
  sourceByMessageId?: Record<string, 'platform' | 'api'>;
}

export type SmsTemplateCategory =
  | 'general'
  | 'marketing'
  | 'cobranza'
  | 'recordatorio'
  | 'soporte'
  | 'otro';

export interface SmsTemplate {
  id: string;
  user_id: string;
  name: string;
  content: string;
  category: SmsTemplateCategory;
  variables: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateSmsTemplateRequest {
  name: string;
  content: string;
  category: SmsTemplateCategory;
  variables?: string[];
}

export interface UpdateSmsTemplateRequest {
  name?: string;
  content?: string;
  category?: SmsTemplateCategory;
  variables?: string[];
  is_active?: boolean;
}
